// AcceptanceDocumentsList — story #73 follow-up / #70 item 6-7: everything uploaded for an acceptance
// version (the signed PDF(s) plus the evidences — PDFs and images) is viewable, downloadable and,
// while the version is open (IN_PROGRESS), deletable per document. Each binary is served through the
// per-document content endpoint (GET .../documents/{documentId}/content — outside the signed-document
// prefix on purpose, since it also serves evidences). The delete action (#70 item 7) lives next to
// view/download on each row and hits DELETE .../documents/{documentId}; it is only offered while the
// acceptance is editable (canDelete).
//
// Two data sources:
//   - live form (default): fetches the CURRENT version's non-deleted documents from
//     GET .../signed-document/versions (Sun scopes it to the current version).
//   - S60 (history drill-down): the caller passes a `documents` array (a past version's non-deleted
//     documents, from GET /{id}/versions) and no fetch happens — same look, read-only (canDelete off).
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material'
import { DeleteOutlineRounded, DownloadRounded, VisibilityRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet, apiDelete } from '../generic/Api_Functions'
import { downloadAcceptanceDocument } from './acceptanceDocumentContent'
import SignedDocumentViewerDialog from './SignedDocumentViewerDialog'

const KIND_LABEL_KEYS = {
  SIGNED: 'Signed document',
  EVIDENCE: 'Evidence'
}

const documentContentPath = (fabricationOrderId, documentId, download) =>
  `fabricationOrderAcceptance/${fabricationOrderId}/documents/${documentId}/content?download=${download}`

export default function AcceptanceDocumentsList ({ fabricationOrderId, refreshToken = 0, canDelete = false, onChanged, documents: providedDocuments }) {
  const { t_ } = useSmartTranslation()
  const [documents, setDocuments] = useState([])
  const [viewing, setViewing] = useState({}) // the document open in the viewer; {} = none

  // #70 item 7 — per-document delete confirmation. `target` = the document being deleted; {} = none.
  const [target, setTarget] = useState({})
  const [reason, setReason] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    // S60 — history drill-down: the caller already has this version's documents (from GET /versions),
    // so use them verbatim and never fetch (that endpoint is scoped to the CURRENT version).
    if (providedDocuments !== undefined) {
      setDocuments((Array.isArray(providedDocuments) ? providedDocuments : []).filter((document) => !document.deletedAt))
      return
    }
    apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/signed-document/versions`)
      .then((versions) => {
        // #70 (point 2 / item 4) — show EVERY non-deleted document the user uploaded for the current
        // acceptance version (Sun already scopes this endpoint to the current version). Each signed
        // document is uploaded as its own file-version, so the list must flatten ALL groups, not just
        // the newest one (`versions[0]`) — otherwise several uploaded signed PDFs would collapse to
        // the last one. The email-generated PDF is never a document here: it lives in the send history.
        const all = (Array.isArray(versions) ? versions : [])
          .flatMap((group) => (Array.isArray(group.documents) ? group.documents : []))
          .filter((document) => !document.deletedAt)
        setDocuments(all)
      })
      .catch(() => setDocuments([]))
  }, [fabricationOrderId, refreshToken, providedDocuments])

  const openDelete = (document) => {
    setTarget(document)
    setReason('')
    setDeleteError('')
  }

  const confirmDelete = () => {
    setDeleting(true)
    setDeleteError('')
    const body = reason.trim() ? { reason: reason.trim() } : {}
    apiDelete(documentDeletePath(fabricationOrderId, target.id), body)
      .then(() => {
        setTarget({})
        if (onChanged) onChanged()
      })
      .catch((error) => {
        const message = (error && (error.message || error.body || error.statusText)) || ''
        setDeleteError(`${t_('The document could not be deleted.')}${message ? ` (${message})` : ''}`)
      })
      .finally(() => setDeleting(false))
  }

  return (
    <>
      {documents.length > 0 && (
        <Box data-testid='acceptance-documents-list' sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
            {t_('Uploaded documents')}
          </Typography>
          <Paper variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Document')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Document type')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>{document.originalFilename}</TableCell>
                    <TableCell>
                      <Chip size='small' label={t_(KIND_LABEL_KEYS[document.kind] || document.kind)} />
                    </TableCell>
                    <TableCell>
                      <Button
                        size='small' startIcon={<VisibilityRounded />}
                        onClick={() => setViewing(document)}
                      >
                        {t_('View')}
                      </Button>
                      <Button
                        size='small' startIcon={<DownloadRounded />}
                        onClick={() => downloadAcceptanceDocument(
                          documentContentPath(fabricationOrderId, document.id, true),
                          document.originalFilename
                        )}
                      >
                        {t_('Download')}
                      </Button>
                      {canDelete && (
                        <Button
                          size='small' color='error' startIcon={<DeleteOutlineRounded />}
                          onClick={() => openDelete(document)}
                        >
                          {t_('Delete')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <SignedDocumentViewerDialog
        open={Boolean(viewing.id)}
        onClose={() => setViewing({})}
        title={viewing.originalFilename || ''}
        contentPath={viewing.id ? documentContentPath(fabricationOrderId, viewing.id, false) : ''}
      />

      {/* #70 item 7 — per-document delete confirmation (optional reason, D3). */}
      <Dialog open={Boolean(target.id)} onClose={() => setTarget({})} fullWidth maxWidth='sm'>
        <DialogTitle>{t_('Delete document')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1.5 }}>
            {t_('This removes the document from this version. The original file is kept for the record.')}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5, overflowWrap: 'anywhere' }}>
            {target.originalFilename || ''}
          </Typography>
          <TextField
            label={t_('Reason (optional)')} multiline minRows={2} fullWidth size='small'
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
          {deleteError && (
            <Alert severity='error' sx={{ mt: 1.5 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget({})} disabled={deleting}>{t_('Cancel')}</Button>
          <Button
            variant='contained' color='error' disabled={deleting} onClick={confirmDelete}
            startIcon={deleting ? <CircularProgress size={16} color='inherit' /> : <DeleteOutlineRounded />}
          >
            {deleting ? t_('Deleting...') : t_('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const documentDeletePath = (fabricationOrderId, documentId) =>
  `fabricationOrderAcceptance/${fabricationOrderId}/documents/${documentId}`
